import {
  AdminHeader,
  AdminSpinner,
  AnimatedDiv,
  Button,
  Disclaimer,
  Div,
  DownloadIcon,
  H3,
  H5,
  Icon,
  ImagePlaceholderIcon,
  ImagePreview,
  P,
  SuccessToast,
  downloadBase64PDF,
  downloadImageFromURL,
  getAgentDetails,
  getFilledW9Form,
  lightTheme,
  queryKeys,
  reportError,
  useUsers,
  verifyAgent,
} from '@vgrubs/components';
import { useInvalidateQueries } from 'components/src/hooks/useInvalidateQueries';
import { AgentDetailsResponse } from 'components/src/types/api/agents.types';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { OpenConversationButton } from '../Chat/OpenConversationButton';
import { KeyValueInfo } from './components/KeyValueInfo';
import { SectionCard } from './components/SectionCard';
import { RejectModalAgent } from './RejectModalAgent';

/**
 * One sales agent waiting for verification. Left: how to reach them and the paperwork.
 * Right: the identity check, ID card and selfie side by side so they can be compared.
 * Verify and Reject sit in the header. Same shape as RestaurantDetails.
 */
export const AgentDetails = () => {
  const [loading, setLoading] = useState(false);
  const [agentDetails, setAgentDetails] = useState<AgentDetailsResponse | null>(
    null
  );
  const navigate = useNavigate();
  const [rejectModal, setRejectModal] = useState<string | number | null>(null);

  const { loading: balanceLoading } = useUsers((state) => state);
  const { invalidateByKey } = useInvalidateQueries();
  const { id } = useParams() as { id: string };

  const agent = agentDetails?.agent;
  const user = agentDetails?.user;
  const fullName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();

  async function handleGetAgentDetails() {
    try {
      setLoading(true);
      const { data } = await getAgentDetails(id);

      setAgentDetails(data.data);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  async function getW9Form() {
    if (!agent || !user) return;
    try {
      setLoading(true);
      const { data } = await getFilledW9Form(agent.id);
      downloadBase64PDF(data.data, `Agent ${fullName} W9 Form`);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  }

  const handleVerifyAgent = async () => {
    if (!agent) return;
    try {
      setLoading(true);
      // TODO: Check if amountPerLocation is required
      const { data } = await verifyAgent(agent?.id);
      SuccessToast(data.message);
      invalidateByKey(queryKeys.admin.verifications.count);
      invalidateByKey(queryKeys.admin.verifications.agents);
      navigate(-1);
    } catch (error) {
      reportError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      handleGetAgentDetails();
    }
  }, []);

  return (
    <>
      <AdminHeader>
        {/* The agent is the subject of this page, so there is no owner to
            reach: the person to ask about a blurry ID card is them. */}
        <OpenConversationButton
          userId={user?.id}
          role={user?.role}
          label="Chat with Agent"
        />
      </AdminHeader>

      {(loading || balanceLoading) && <AdminSpinner />}
      {agentDetails && (
        <>
          <Div
            $flex
            $columnSM
            $gap="32px"
            $gapSM="20px"
            $m="32px 0"
            $mSM="20px 0"
            $between
            $alignCenter
          >
            <Div>
              <H3 $mb="8" $color={lightTheme.primary}>
                {fullName}
              </H3>
              <P $m="0">Sales agent</P>
            </Div>

            <Div
              $flex
              $alignEnd
              $justifyCenter
              $column
              $gap="16px"
              $widthSM="100%"
            >
              <Button
                $selected
                $gray
                $w="250px"
                $wSM="100%"
                onClick={() => setRejectModal(agent?.id ?? null)}
              >
                Reject Agent
              </Button>
              <Button
                $selected
                $w="250px"
                $wSM="100%"
                onClick={handleVerifyAgent}
              >
                Verify Agent
              </Button>
            </Div>
          </Div>

          <AnimatedDiv $flex $columnSM $gap="32px" $gapSM="24px">
            <Div $width="40%" $widthSM="100%" $flex $column $gap="20px">
              <P $m="0" $bold $color={lightTheme.lightGray}>
                Submitted by the agent
              </P>

              <SectionCard title="Contact">
                <a href={`mailto:${user?.email}`}>
                  <KeyValueInfo k="Email" v={user?.email} />
                </a>
                <a href={`tel:${user?.phoneNumber}`}>
                  <KeyValueInfo k="Phone" v={user?.phoneNumber} last />
                </a>
              </SectionCard>

              <SectionCard
                title="W9 Form"
                action={
                  <Icon $pointer onClick={getW9Form}>
                    <DownloadIcon />
                  </Icon>
                }
              >
                <P $m="0" $color={lightTheme.darkGray}>
                  Filled in and signed by the agent. Download to review.
                </P>
              </SectionCard>
            </Div>

            <Div $width="60%" $widthSM="100%" $flex $column $gap="20px">
              <P $m="0" $bold $color={lightTheme.lightGray}>
                Identity check
              </P>

              <SectionCard title="ID card and selfie">
                <Div $flex $columnSM $gap="20px">
                  <Div $width="62%" $widthSM="100%">
                    <DocumentPreview
                      label="ID card"
                      image={agent?.idCardFrontUrl}
                      onDownload={() =>
                        downloadImageFromURL(
                          agent?.idCardFrontUrl,
                          `Agent ${fullName} ID Card.png`
                        )
                      }
                    />
                  </Div>
                  <Div $width="38%" $widthSM="100%">
                    <DocumentPreview
                      label="Selfie"
                      image={agent?.selfieUrl}
                      onDownload={() =>
                        downloadImageFromURL(
                          agent?.selfieUrl,
                          `Agent ${fullName} Selfie`
                        )
                      }
                    />
                  </Div>
                </Div>
              </SectionCard>
            </Div>
          </AnimatedDiv>
        </>
      )}
      <RejectModalAgent
        modalIsOpen={rejectModal ?? ''}
        closeModal={() => setRejectModal(null)}
        setLoading={setLoading}
      />
    </>
  );
};

/** One identity image with its own title row and download, or a placeholder when missing. */
const DocumentPreview = ({
  label,
  image,
  onDownload,
}: {
  label: string;
  image: string | null | undefined;
  onDownload: () => void;
}) => (
  <>
    <Div $flex $alignCenter $between $mb="10">
      <H5 $m="0">{label}</H5>
      {image && (
        <Icon $pointer onClick={onDownload}>
          <DownloadIcon />
        </Icon>
      )}
    </Div>
    <Div
      $box
      $boxNoShadow
      $p="12px"
      $height="300px"
      $heightSM="260px"
      $flex
      $center
    >
      {image ? (
        <ImagePreview
          image={image}
          borderRadius="10px"
          style={{ objectFit: 'contain', width: '100%', height: '100%' }}
        />
      ) : (
        <Div $flex $column $alignCenter>
          <ImagePlaceholderIcon />
          <Disclaimer $center $m="0">
            Not uploaded yet
          </Disclaimer>
        </Div>
      )}
    </Div>
  </>
);
